import React, { useState, useEffect } from 'react';
import './NumberInputSection.css';

function NumberInputSection({ 
    label, 
    fieldName, 
    value, 
    min = 0, 
    max = 100, 
    step = 1, 
    unit = '', 
    onChange,
    icon,
    disabled = false,
    showSlider = false,
    color = '#5C80BC',
    description,
    placeholder
}) {
    const [displayValue, setDisplayValue] = useState(value || min);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        setDisplayValue(value || min);
    }, [value, min]);

    // Validate and clamp value
    const validateValue = (val) => {
        const numVal = parseInt(val) || min;
        return Math.min(Math.max(numVal, min), max);
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setDisplayValue(newValue);
        
        // Validate on blur or if it's a complete number
        if (!isNaN(newValue) && newValue !== '') {
            const validatedValue = validateValue(newValue);
            if (onChange) {
                onChange(fieldName, validatedValue);
            }
        }
    };

    const handleInputBlur = (e) => {
        const validatedValue = validateValue(e.target.value);
        setDisplayValue(validatedValue);
        if (onChange) {
            onChange(fieldName, validatedValue);
        }
    };

    const handleSliderChange = (e) => {
        const newValue = parseInt(e.target.value);
        setDisplayValue(newValue);
        if (onChange) {
            onChange(fieldName, newValue);
        }
    };

    const handleSliderMouseDown = () => {
        setIsDragging(true);
    };

    const handleSliderMouseUp = () => {
        setIsDragging(false);
    };

    // Calculate percentage for slider
    const percentage = ((displayValue - min) / (max - min)) * 100;

    return (
        <div className={`number-input-section ${disabled ? 'disabled' : ''}`}>
            {/* Header */}
            <div className="number-input-header">
                <label className="number-input-label">
                    {label}
                </label>
                
                {/* Value Display with Unit */}
                <div className="number-input-value-display">
                    <input
                        type="number"
                        name={fieldName}
                        value={displayValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        min={min}
                        max={max}
                        step={step}
                        disabled={disabled}
                        className="number-input-field"
                        placeholder={placeholder}
                    />
                    {unit && <span className="number-input-unit">{unit}</span>}
                </div>
            </div>

            {/* Description */}
            {description && (
                <p className="number-input-description">{description}</p>
            )}

            {/* Optional Slider */}
            {showSlider && (
                <div className="number-input-slider-container">
                    {/* Min/Max Labels */}
                    <div className="slider-range-labels">
                        <span className="range-label min">{min}{unit}</span>
                        <span className="range-label max">{max}{unit}</span>
                    </div>

                    {/* Slider Track */}
                    <div className="slider-track-container">
                        <input
                            type="range"
                            min={min}
                            max={max}
                            step={step}
                            value={displayValue}
                            onChange={handleSliderChange}
                            onMouseDown={handleSliderMouseDown}
                            onMouseUp={handleSliderMouseUp}
                            disabled={disabled}
                            className={`slider-input ${isDragging ? 'dragging' : ''}`}
                            style={{
                                '--slider-percentage': `${percentage}%`,
                                '--slider-color': color
                            }}
                        />
                        
                        {/* Custom Track Background */}
                        <div className="slider-track">
                            <div 
                                className="slider-progress"
                                style={{ 
                                    width: `${percentage}%`,
                                    backgroundColor: color
                                }}
                            />
                        </div>

                        {/* Custom Thumb */}
                        <div 
                            className={`slider-thumb ${isDragging ? 'dragging' : ''}`}
                            style={{ 
                                left: `${percentage}%`,
                                borderColor: color
                            }}
                        >
                            <div className="slider-thumb-inner" style={{ backgroundColor: color }} />
                        </div>
                    </div>

                    {/* Dragging Tooltip */}
                    <div className="slider-value-indicator">
                        <div 
                            className="value-tooltip"
                            style={{ 
                                left: `${percentage}%`,
                                opacity: isDragging ? 1 : 0
                            }}
                        >
                            {displayValue}{unit}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NumberInputSection;
